// @ts-nocheck
import * as React from "react";
import { Ic, RoleTabBar, useCurrentUser } from "./common";
import { sos as sosApi } from "../api";

// 응급SOS 접수 화면 — 어르신/보호자 전용. 원본(통합돌봄 gcare)의 citizen_sos를
// 이 프로젝트 규약(로그인 기반·design-token·CaseRecord sos 플래그)으로 이식.
// 어르신 정보는 로그인으로 자동 해결되므로 입력은 ① SOS 유형 ② 상황 설명만.
// 제출 → 즉시 critical 케이스 생성 + 응급기관 퀵매칭 + 권역 운영자 긴급알림.

// 색은 앱 디자인 토큰(따뜻한 어스톤)에서만 — 위급(낙상/긴급/학대)은 rose 계열,
// 그 외(배회/미응답/정신건강)는 sky·orange 로 구분. 쨍한 빨강/보라 금지.
const SOS_TYPES = [
  { key: "fall",      label: "낙상·외상",     icon: "alert",    color: "#C9594F" }, // rose-500
  { key: "wander",    label: "배회·실종",     icon: "map",      color: "#5C82A8" }, // sky-500
  { key: "no_answer", label: "안부 미응답",   icon: "phone",    color: "#D26A44" }, // orange-600
  { key: "urgent",    label: "긴급돌봄 필요", icon: "schedule", color: "#A8453C" }, // rose-600
  { key: "mental",    label: "정신건강 위기", icon: "user",     color: "#436A8E" }, // sky-600
  { key: "abuse",     label: "학대 의심",     icon: "heart",    color: "#823129" }, // rose-700
];
// SOS_LABEL은 6종 전부 유지 — 최근 SOS 내역엔 보호자가 올린 유형(배회·미응답 등)도
// 같은 어르신 기준으로 함께 보이므로, 어르신 화면에서도 전체 키의 한글 라벨이 필요하다.
const SOS_LABEL = Object.fromEntries(SOS_TYPES.map((t) => [t.key, t.label]));

// 어르신(citizen) 본인 관점의 유형 — 스스로 누를 수 있는 것만 + 1인칭 라벨.
// 제외: wander(배회·실종)·no_answer(안부 미응답)는 본인이 아니라 '곁에서 지켜보는 사람'이
// 인지하는 3인칭 상황이라 어르신 화면 선택지에선 숨긴다(보호자 화면엔 6종 그대로 노출).
const CITIZEN_TYPE_KEYS = ["fall", "urgent", "mental", "abuse"];
const CITIZEN_LABEL = {
  fall:   "넘어졌어요",
  urgent: "도움이 필요해요",
  mental: "마음이 힘들어요",
  abuse:  "위협받고 있어요",
};
const SOS_EMERGENCY_TEL = "119";

function SbarG() {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 22px 4px", fontSize: 11, fontWeight: 600, color: "var(--ink-1)" }}>
      <span>{new Date().toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" })}</span>
      <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
        <span>●●●</span><span style={{ fontSize: 10 }}>5G</span>
        <span style={{ width: 18, height: 9, border: "1px solid var(--ink-1)", borderRadius: 2, position: "relative" }}><span style={{ position: "absolute", inset: 1, background: "var(--ink-1)" }} /></span>
      </div>
    </div>
  );
}
const HbarG = () => <div style={{ height: 4, width: 120, background: "var(--ink-1)", borderRadius: 99, margin: "2px auto 6px" }} />;

function humanSosError(e) {
  const msg = e?.message || "";
  if (msg.includes(" 401 ")) return "로그인이 필요합니다.";
  if (msg.includes(" 403 ")) return "권한이 없습니다.";
  const match = msg.match(/\{.*\}$/);
  if (match) {
    try {
      const body = JSON.parse(match[0]);
      if (body.message) return body.message;
      if (Array.isArray(body.details) && body.details.length) return body.details.join(", ");
      if (body.error) return body.error;
    } catch { /* fall through */ }
  }
  return "SOS 접수에 실패했습니다. 잠시 후 다시 시도하거나 119로 전화해 주세요.";
}

// 접수 후 중복(스팸) 접수 방지 쿨다운 — 5분. 같은 응급 상황에 대한 연타·중복 접수를
// 막는다(진짜 위급 시엔 항상 119 전화가 별도로 열려 있다). 새로고침/재진입에도 유지되도록
// localStorage에 만료시각을 저장하고, 더해서 최근 SOS의 서버 created_at 기준으로도 계산해
// (기기/캐시 비우기와 무관하게) 서버 진실로 막는다.
const SOS_COOLDOWN_MS = 5 * 60 * 1000;
const sosCooldownKey = (userId) => `care-os.sosCooldownUntil.${userId || "anon"}`;
function loadSosCooldownUntil(userId) {
  if (typeof window === "undefined") return 0;
  try { return parseInt(window.localStorage.getItem(sosCooldownKey(userId)) || "0", 10) || 0; }
  catch { return 0; }
}
function saveSosCooldownUntil(userId, ts) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(sosCooldownKey(userId), String(ts)); } catch { /* quota/blocked */ }
}
// 남은 시간 mm:ss
function fmtCooldown(ms) {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function Sos() {
  const { user } = useCurrentUser();
  const isCitizen = user?.role === "citizen";
  // 어르신 본인은 1인칭 큐레이션 유형만, 보호자는 6종 전체. fall이 기본값이라 두 목록
  // 모두 포함 — 역할 로딩 전 잠깐 6종이 보여도 선택값(fall)은 양쪽에 유효해 안전하다.
  const types = isCitizen
    ? SOS_TYPES.filter((t) => CITIZEN_TYPE_KEYS.includes(t.key)).map((t) => ({ ...t, label: CITIZEN_LABEL[t.key] || t.label }))
    : SOS_TYPES;
  const [sosType, setSosType] = React.useState("fall");
  const [situation, setSituation] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [result, setResult] = React.useState(null); // 접수 성공 결과
  const [err, setErr] = React.useState(null);
  const [recent, setRecent] = React.useState([]);

  const loadRecent = React.useCallback(() => {
    sosApi.recent().then((r) => setRecent(Array.isArray(r) ? r : [])).catch(() => {});
  }, []);
  React.useEffect(() => { loadRecent(); }, [loadRecent]);

  // ── 접수 후 5분 쿨다운 ────────────────────────────────────────────────
  const [cooldownUntil, setCooldownUntil] = React.useState(0);
  const [nowTs, setNowTs] = React.useState(() => Date.now());
  // 사용자 확정 시 localStorage 쿨다운 로드(계정별 키).
  React.useEffect(() => {
    if (!user?.id) return;
    const v = loadSosCooldownUntil(user.id);
    if (v) setCooldownUntil((prev) => Math.max(prev, v));
  }, [user?.id]);
  // 최근 SOS의 서버 created_at 기준 쿨다운 — 캐시 비우기/다른 기기에도 막힌다.
  React.useEffect(() => {
    if (!recent.length) return;
    const latest = recent.reduce((mx, c) => {
      const t = new Date(c.created_at).getTime();
      return Number.isFinite(t) ? Math.max(mx, t) : mx;
    }, 0);
    if (latest) setCooldownUntil((prev) => Math.max(prev, latest + SOS_COOLDOWN_MS));
  }, [recent]);
  const cooldownRemainingMs = Math.max(0, cooldownUntil - nowTs);
  const inCooldown = cooldownRemainingMs > 0;
  // 쿨다운 동안만 1초 틱으로 카운트다운 갱신.
  React.useEffect(() => {
    if (cooldownUntil <= Date.now()) return undefined;
    const id = window.setInterval(() => setNowTs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [cooldownUntil]);

  const submit = async () => {
    if (submitting || inCooldown) return; // 접수 중·쿨다운 중엔 중복 접수 차단
    setErr(null);
    setSubmitting(true);
    try {
      const r = await sosApi.submit({ sos_type: sosType, situation });
      // 접수 성공 → 5분 쿨다운 시작(성공 화면 전환 후·재진입 시 폼 버튼이 비활성).
      const until = Date.now() + SOS_COOLDOWN_MS;
      saveSosCooldownUntil(user?.id, until);
      setCooldownUntil((prev) => Math.max(prev, until));
      setResult(r);
      loadRecent();
    } catch (e) {
      setErr(humanSosError(e));
    } finally {
      setSubmitting(false);
    }
  };

  // 로그인 사용자의 역할 홈(guardian→/dashboard/guardian, citizen→/home).
  // /m(모바일 마케팅 랜딩)으로 보내면 로그인 어르신이 랜딩을 보게 되므로 금지.
  const homePath = user?.default_path || (user?.role === "citizen" ? "/home" : "/dashboard/guardian");
  if (result) return <SosSuccess result={result} homePath={homePath} />;

  return (
    <div className="app" style={{ background: "var(--bg)", overflow: "auto", display: "flex", flexDirection: "column" }}>
      <SbarG />

      {/* 헤더 */}
      <div style={{ padding: "8px 22px 6px", display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
        <a onClick={() => history.back()} aria-label="뒤로"
           style={{ width: 36, height: 36, display: "grid", placeItems: "center", borderRadius: 11, background: "var(--surface)", color: "var(--ink-1)", fontSize: 20, cursor: "pointer", textDecoration: "none" }}>‹</a>
        <div style={{ fontSize: 18, fontWeight: 800 }}>응급SOS 신청</div>
      </div>

      {/* 긴급 안내 배너 — 홈 히어로 카드와 동일한 따뜻한 다크 그라데이션 + rose 강조.
          flexShrink:0 필수 — overflow:hidden 자식은 flex min-height가 0이 돼, 콘텐츠가
          길어지면(최근 SOS 리스트 등) 배너가 압축돼 설명/119 줄이 잘렸다. */}
      <div style={{ margin: "8px 22px 0", padding: 16, borderRadius: 18, background: "linear-gradient(135deg, #2A241F, #3E3329)", color: "#F5EBD8", position: "relative", overflow: "hidden", flexShrink: 0 }}>
        <div style={{ position: "absolute", top: -40, right: -30, width: 140, height: 140, borderRadius: "50%", background: "rgba(220,139,130,0.16)" }} />
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, background: "linear-gradient(135deg, #DC8B82, #C9594F)", display: "grid", placeItems: "center", flex: "0 0 auto" }}>
            <span style={{ width: 24, height: 24, display: "inline-block", color: "#fff" }}>{Ic.alert}</span>
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#fff" }}>응급SOS 퀵매칭</div>
            <div style={{ fontSize: 11.5, color: "#C9B7A6", marginTop: 3, lineHeight: 1.45 }}>낙상·외상은 인근 소방기관에 자동 연계하고<br />그 외 긴급상황은 담당 케어매니저(미배정 시 권역 담당자)에게 알립니다.</div>
          </div>
        </div>
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px dashed rgba(245,235,216,0.22)", fontSize: 11.5, color: "#C9B7A6" }}>
          생명이 위급하면 먼저 <a href={`tel:${SOS_EMERGENCY_TEL}`} style={{ color: "#DC8B82", fontWeight: 800, textDecoration: "underline" }}>119</a>로 전화하세요.
        </div>
      </div>

      {err && <div role="alert" style={{ margin: "14px 22px 0", padding: 12, background: "var(--rose-50)", color: "var(--rose-500)", borderRadius: 10, fontSize: 12.5 }}>{err}</div>}

      {/* SOS 유형 */}
      <div style={{ padding: "18px 22px 0" }}>
        <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 10 }}>어떤 상황인가요?</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
          {types.map((t) => {
            const on = sosType === t.key;
            return (
              <button key={t.key} type="button" onClick={() => setSosType(t.key)}
                aria-pressed={on}
                style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "14px 4px", borderRadius: 14,
                         background: on ? "var(--rose-50)" : "var(--surface)", border: on ? `1.5px solid ${t.color}` : "1px solid var(--border)",
                         cursor: "pointer", boxShadow: on ? `0 0 0 3px ${t.color}22` : "none", transition: "all .12s" }}>
                <span style={{ width: 38, height: 38, borderRadius: 12, background: `${t.color}1f`, display: "grid", placeItems: "center", color: t.color }}>
                  <span style={{ width: 18, height: 18, display: "inline-block" }}>{Ic[t.icon] || Ic.alert}</span>
                </span>
                <span style={{ fontSize: 12, fontWeight: 700, color: "var(--ink-1)", textAlign: "center", lineHeight: 1.2 }}>{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 상황 설명 */}
      <div style={{ padding: "18px 22px 0" }}>
        <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 10 }}>상황 설명 <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-3)" }}>(선택)</span></div>
        <textarea className="input" rows={3} value={situation} onChange={(e) => setSituation(e.target.value)}
          placeholder={isCitizen
            ? "예: 화장실에서 넘어져 일어나기가 힘들어요. 평소 혈압이 높습니다."
            : "예: 어머니가 30분째 전화를 안 받으세요. 평소 혈압이 높으셔서 걱정됩니다."}
          style={{ resize: "vertical", fontFamily: "inherit" }} />
      </div>

      {/* 안내 — 어르신 정보는 로그인으로 자동 첨부 */}
      <div style={{ padding: "12px 22px 0" }}>
        <div className="muted" style={{ fontSize: 11.5, lineHeight: 1.5 }}>
          {user?.name ? <><b>{user.name}</b>님 계정에 </> : ""}등록된 {isCitizen ? "회원님" : "어르신"} 정보(성함·권역·보호자 연락처)가 자동으로 함께 접수됩니다.
        </div>
      </div>

      {/* 제출 */}
      <div style={{ padding: "18px 22px 8px" }}>
        {(() => {
          const disabled = submitting || inCooldown;
          return (
            <button type="button" onClick={submit} disabled={disabled}
              aria-disabled={disabled}
              title={inCooldown ? "최근 접수 후 5분간 중복 접수가 제한됩니다" : undefined}
              style={{ width: "100%", padding: "16px 0", borderRadius: 14, border: "none", cursor: disabled ? "default" : "pointer",
                       background: disabled ? "var(--ink-4)" : "linear-gradient(135deg, var(--rose-500), var(--rose-600))", color: "#fff",
                       fontSize: 16, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                       boxShadow: disabled ? "none" : "0 14px 30px -10px rgba(201,89,79,0.5)" }}>
              <span style={{ width: 18, height: 18, display: "inline-block" }}>{Ic.alert}</span>
              {submitting ? "접수 중…"
                : inCooldown ? `재접수 가능까지 ${fmtCooldown(cooldownRemainingMs)}`
                : "응급SOS 접수 · 즉시 퀵매칭"}
            </button>
          );
        })()}
        {inCooldown && (
          <div role="status" style={{ marginTop: 10, padding: "10px 12px", background: "var(--rose-50)", color: "var(--rose-700)", borderRadius: 12, fontSize: 11.5, lineHeight: 1.5, fontWeight: 600 }}>
            방금 SOS를 접수했습니다. 중복 접수를 막기 위해 <b>5분간</b> 비활성화됩니다(남은 시간 {fmtCooldown(cooldownRemainingMs)}). 위급하면 즉시 <a href={`tel:${SOS_EMERGENCY_TEL}`} style={{ color: "var(--rose-700)", fontWeight: 800 }}>{SOS_EMERGENCY_TEL}</a>로 전화하세요.
          </div>
        )}
      </div>

      {/* 최근 SOS 내역 */}
      {recent.length > 0 && (
        <div style={{ padding: "10px 22px 0" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink-3)", margin: "8px 0 8px" }}>최근 SOS 접수</div>
          <div className="card" style={{ padding: "4px 16px", borderRadius: 14 }}>
            {recent.map((c, i) => (
              <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 0", borderTop: i > 0 ? "1px solid var(--border-soft)" : "none" }}>
                <span style={{ width: 30, height: 30, borderRadius: 9, background: "var(--rose-50)", color: "var(--rose-500)", display: "grid", placeItems: "center", flex: "0 0 auto" }}>
                  <span style={{ width: 15, height: 15, display: "inline-block" }}>{Ic.alert}</span>
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink-1)" }}>{(isCitizen && CITIZEN_LABEL[c.sos_type]) || SOS_LABEL[c.sos_type] || c.sos_type}</div>
                  <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {c.matched_org ? c.matched_org.name : "매칭 대기"} · {new Date(c.created_at).toLocaleString("ko-KR", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
                <span className={"badge " + (c.quick_matched ? "badge-green" : "badge-amber")} style={{ fontSize: 11 }}>{c.quick_matched ? "매칭완료" : "대기"}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ height: 14 }} />
      <div style={{ marginTop: "auto" }}><RoleTabBar active="home" /></div>
      <HbarG />
    </div>
  );
}

// 접수 성공 화면 — 퀵매칭 결과를 보여주고 홈으로 돌아갈 동선을 준다.
// 기관 카테고리 한글 라벨(org-management의 CAT_LABEL과 동일 관례). 현재 SOS 자동연계는
// 낙상·외상 → 소방(fire)만이라 사실상 "소방"이지만, 표시는 실제 category에서 끌어온다.
const CAT_LABEL = { fire: "소방", hospital: "병원", health: "보건소", welfare: "복지", care: "요양" };

function SosSuccess({ result, homePath = "/dashboard/guardian" }) {
  const org = result?.matched_org;
  const catLabel = org ? (CAT_LABEL[org.category] || "응급기관") : null;
  const isFall = result?.sos_type === "fall";
  // 담당 케어매니저에게 실제로 알림이 갔을 때만 문구에 포함 — 낙상도 매니저 미배정이면
  // 매니저 통지가 없으므로 '담당 케어매니저' 문구를 빼고 권역 담당자만 안내한다.
  const mgrPart = result?.manager_notified ? "와 담당 케어매니저" : "";
  return (
    <div className="app" style={{ background: "var(--bg)", display: "flex", flexDirection: "column", height: "100%" }}>
      <SbarG />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 24px", textAlign: "center" }}>
        <div style={{ width: 72, height: 72, borderRadius: "50%", background: "var(--green-50)", color: "var(--green-600)", display: "grid", placeItems: "center", fontSize: 36, marginBottom: 18 }}>✓</div>
        <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>응급SOS가 접수되었습니다</div>
        {result?.deduped && (
          // 이미 처리 중인 SOS가 있어 새 케이스를 만들지 않고 기존 건에 합쳤음을 안내.
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 12px", borderRadius: 999,
                        background: "var(--bg-sub)", color: "var(--ink-2)", fontSize: 12, fontWeight: 700, marginBottom: 10 }}>
            이미 접수된 응급SOS에 합쳐졌어요 · 중복 신청은 만들지 않았습니다
          </div>
        )}
        {org && (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 12px", borderRadius: 999,
                        background: "var(--orange-50)", color: "var(--orange-600)", fontSize: 12.5, fontWeight: 800, marginBottom: 10 }}>
            <span style={{ width: 14, height: 14, display: "inline-block" }}>{Ic.alert}</span>
            {catLabel} 기관 자동연계
          </div>
        )}
        {org ? (
          <div className="muted" style={{ fontSize: 13.5, lineHeight: 1.6, maxWidth: 300 }}>
            {catLabel} 기관 <b style={{ color: "var(--ink-1)" }}>{org.name}</b>에 자동 연계되었습니다.<br />
            권역 담당자{mgrPart}에게 즉시 알림이 전달되었어요.
          </div>
        ) : isFall ? (
          <div className="muted" style={{ fontSize: 13.5, lineHeight: 1.6, maxWidth: 300 }}>
            인근 소방기관을 찾지 못해 연계 대기 중입니다.<br />권역 담당자{mgrPart}가 직접 대응합니다.
          </div>
        ) : result?.responder === "region" ? (
          // 담당 케어매니저 배정 전(매칭 전)이라 매니저가 아닌 권역 담당자에게 폴백 통지됨.
          <div className="muted" style={{ fontSize: 13.5, lineHeight: 1.6, maxWidth: 300 }}>
            아직 담당 케어매니저 배정 전이라,<br />권역 담당자에게 먼저 알림이 전달되었어요.<br />곧 담당자를 배정해 대응합니다.
          </div>
        ) : (
          <div className="muted" style={{ fontSize: 13.5, lineHeight: 1.6, maxWidth: 300 }}>
            담당 케어매니저에게 즉시 알림이 전달되었어요.<br />담당자가 직접 확인하고 대응합니다.
          </div>
        )}
        {org?.emergency_phone && (
          <a href={`tel:${org.emergency_phone}`} className="btn btn-secondary btn-lg"
             style={{ marginTop: 20, textDecoration: "none", minWidth: 220, display: "inline-flex", justifyContent: "center", alignItems: "center", gap: 8 }}>
            <span style={{ width: 18, height: 18, display: "inline-block" }}>{Ic.phone}</span>
            {org.name} 전화 ({org.emergency_phone})
          </a>
        )}
        <a href={homePath} className="btn btn-primary btn-lg" style={{ marginTop: 12, textDecoration: "none", minWidth: 220, display: "inline-flex", justifyContent: "center" }}>홈으로 →</a>
      </div>
      <HbarG />
    </div>
  );
}

export { Sos };
